import { useFpB2bCalculateResultWithTransactionInfo } from '@cart-services/hooks/fp-b2b/use-fp-b2b-calculate-result-with-transaction-info'
import { useFpB2bPaymentService } from '@cart-services/hooks/fp-b2b/use-fp-b2b-payment-service'
import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { useApiGetFpPayment } from '@hooks-api/fortune/useApiGetFpPayment'
import { type CheckoutOrder } from '@hooks-api/types/dataModel/orders'
import { PaymentMethodType, PaymentServiceType } from '@hooks-api/types/dataModel/payment'
import { TappayApplePayModule } from '@payments/lib/TappayApplePayModule'
import { Box } from '@pets-ui/Box/Box'
import { HStack } from '@pets-ui/HStack/HStack'
import { ApplePay } from '@pets-ui/icons'
import { Radio } from '@pets-ui/Radio/Radio'
import { Text } from '@pets-ui/Text/Text'
import { VStack } from '@pets-ui/VStack/VStack'
import { useCallback, useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import { useParams } from 'react-router-dom-v5'
import { useContextPayment } from '../../../../fp-b2b-shared/components/PaymentProvider/PaymentProvider'
import { useCheckout } from '../../../../fp-b2b-shared/hooks/use-checkout'
import { paymentComponentStyle } from './PaymentMethodComponent'

export const PaymentTappayApplePay = () => {
  const { watch } = useFormContext<CheckoutOrder>()
  const watchBillingCountry = watch('billing_country')
  const { selectedPayment, setSelectedPayment, paymentMethods, setPaymentExecution, getActivePaymentModuleB2B, setConfiguredPaymentModule, isSdkInitialized, setIsLoadingPayment, isProcessingCheckout } = useContextPayment()
  const { trigger: getPaymentToken } = useApiGetFpPayment()
  const { env, campaignId, campaignCode, currency, platform } = useCheckout()

  const checked = selectedPayment?.service_type === PaymentServiceType.TAPPAY && selectedPayment.type === PaymentMethodType.APPLE_PAY
  const currentMethod = paymentMethods.find(method => method.service_type === PaymentServiceType.TAPPAY && method.type === PaymentMethodType.APPLE_PAY)
  const paymentModule = getActivePaymentModuleB2B(PaymentServiceType.TAPPAY, PaymentMethodType.APPLE_PAY)

  const { transactionId: paymentTransactionIdFromUrl } = useParams<{ transactionId?: string }>()
  const { orderId, orderType } = useFpB2bPaymentService({ campaignId, paymentTransactionIdFromUrl, campaignCode, platform })
  // retry 頁購物車已清空，改從 transaction info 取得實際 total；一般結帳頁走 cart calculate
  const { calculateResultWithTransactionInfo } = useFpB2bCalculateResultWithTransactionInfo(campaignId, campaignCode, platform, paymentTransactionIdFromUrl)
  const total = calculateResultWithTransactionInfo?.total

  const handleSelectPayment = useCallbackEvent(async () => {
    try {
      if (!campaignCode || !total) {
        throw new Error('Missing campaign code, total, selectedPayment')
      }
      if (!currentMethod) {
        throw new Error('Missing payment method')
      }
      setSelectedPayment(currentMethod)

      setIsLoadingPayment(true)
      const token = await getPaymentToken({
        order_type: orderType,
        order_id: orderId,
        payment_method_id: currentMethod.id,
      })

      const { app_id, app_key, app_pay_identifier } = token.data.data.payment_service_provider_info
      if (!app_id || !app_key || !app_pay_identifier) {
        throw new Error('Missing app_id, app_key, app_pay_identifier')
      }
      if (!paymentModule || !(paymentModule instanceof TappayApplePayModule)) {
        throw new Error('Wrong module')
      }
      paymentModule.configurePayment({
        appId: String(app_id),
        appKey: app_key,
        appEnv: env,
        currency,
        countryCode: watchBillingCountry,
        orderTotalAmount: total,
        merchantIdentifier: app_pay_identifier,
        displayItems: [{
          label: '總計',
          amount: {
            currency,
            value: String(total),
          },
        }],
        onCancel: () => {
          alert('偵測到交易己取消，請前往「設定」確認Apple Pay及信用卡相關設定正確無誤。')
        },
      })
      const { payment_transaction_id } = token.data.data
      setPaymentExecution({
        payment_transaction_id,
        service_pay_info: null,
        user_payment_token_info: undefined,
        payment_success_url: '',
        payment_fallback_url: '',
      })
      setConfiguredPaymentModule(paymentModule)
    }
    catch (e) {
      console.error(`[Apple Pay Tappay] ${e}`)
    }
    finally {
      setIsLoadingPayment(false)
    }
  })
  const handleInitPayment = useCallback(async () => {
    // NOTE: isSdkInitialized promises that the payment module is init after sdk init
    const shouldInit = selectedPayment?.service_type === PaymentServiceType.TAPPAY && selectedPayment.type === PaymentMethodType.APPLE_PAY && total && isSdkInitialized
    if (!shouldInit) return
    handleSelectPayment()
  }, [handleSelectPayment, isSdkInitialized, selectedPayment?.service_type, selectedPayment?.type, total])

  // NOTE: init for default payment
  useEffect(() => {
    handleInitPayment()
  }, [handleInitPayment])
  return (
    <Box component="label" rootStyle={[paymentComponentStyle.root, paymentComponentStyle.radioPaymentChecked]}>
      <VStack flexGrow={1} gap={2.5}>
        <HStack alignItems="center" gap={2}>
          <Radio
            name="paymentTapPayApplePay"
            checked={checked}
            value="paymentTapPayApplePay"
            onChange={handleSelectPayment}
            disabled={isProcessingCheckout}
          />
          <ApplePay />
          <Text fontWeight={600} lineHeight="100%">
            Apple Pay
          </Text>
          <Box flexGrow={1} />
          {/* endIcon */}
        </HStack>
      </VStack>
    </Box>
  )
}
